import { validStr } from 'booksprout'

/**
 * Help Scout Beacon identity handling, shared by the author and reviewer apps.
 *
 * The Beacon loader and `init` still live in each app's index.html because they have to run
 * before the bundle does. Everything that depends on *who* is signed in lives here so both apps get
 * the same behaviour from one implementation.
 *
 * Beacon keys a customer on their email address: `identify` creates or updates the profile for that
 * address, and it is the only thing that ties a conversation back to a Booksprout account. We used to
 * pass the custom attributes to `identify` and the name/email to `prefill`, but `prefill` only fills
 * the "Create a message" contact form. AI Answers conversations never reach that form, so those
 * threads arrived as "Unidentified Customer" with no email to look the account up by [sc-27755].
 */

/**
 * How Beacon is called: a method name plus an optional payload. `window.Beacon` is only typed as a
 * bare `Function` globally, because the snippet installs it before anything here can describe it.
 */
type BeaconApi = (method: string, options?: unknown) => unknown

export interface BeaconUserData {
  name?: string
  emailAddress?: string
  plan?: string
  userSince?: string
  stripeUrl?: string
  adminUrl?: string
}

/**
 * The address we last handed to Beacon, so a second sign in on the same tab can clear the previous
 * visitor first. Module level state is safe here because every entry point no-ops during SSR, so
 * this is only ever touched in the browser where one module instance means one page.
 */
let identifiedEmailAddress: string | undefined = void 0

/**
 * Set when we are asked to identify before the Beacon snippet has run, so a bundle that happens to
 * execute first (SSR can order the body differently to a static build) still identifies the user.
 */
let pendingUser: BeaconUserData | undefined = void 0
let flushOnLoadBound = false

const getBeacon = (): BeaconApi | undefined => {
  if (process.env.SERVER || typeof window === 'undefined') {
    return void 0
  }

  // Absent in the testing env and in the Capacitor builds, both of which skip the snippet entirely.
  return typeof window.Beacon === 'function' ? window.Beacon as BeaconApi : void 0
}

/**
 * Beacon's own loader defines `window.Beacon` synchronously and queues calls made before its script
 * arrives, so the only gap we have to cover is the snippet not having run at all yet. `load` is the
 * latest point it could run by, and we bind it once.
 */
const flushWhenBeaconLoads = () => {
  if (flushOnLoadBound || typeof window === 'undefined') {
    return
  }

  flushOnLoadBound = true
  window.addEventListener('load', () => {
    const user = pendingUser
    if (user !== void 0) {
      pendingUser = void 0
      identifyBeaconUser(user)
    }
  }, { once: true })
}

/**
 * Tell Help Scout which Booksprout account is using the app so support can tie a thread, including
 * an AI Answers thread that never reaches the contact form, back to the user.
 */
export const identifyBeaconUser = (user: BeaconUserData) => {
  // Without an email there is no customer for Beacon to create or match, and the attributes would be
  // stranded on an anonymous visitor - which is the whole bug this replaces.
  if (!validStr(user.emailAddress)) {
    return
  }

  const beacon = getBeacon()
  if (beacon === void 0) {
    pendingUser = user
    flushWhenBeaconLoads()
    return
  }

  const emailAddress = user.emailAddress as string

  if (identifiedEmailAddress !== void 0 && identifiedEmailAddress !== emailAddress) {
    // A different account is now in use on this tab. Beacon holds onto identify data and conversation
    // history until it is logged out, so without this the new session stays attached to the old user.
    beacon('logout')
  }

  beacon('identify', {
    // Omitted rather than sent empty: a blank name would overwrite one Help Scout already holds for
    // this address (the server side customer builder makes the same call).
    ...(validStr(user.name) ? { name: user.name } : {}),
    email: emailAddress,
    'admin-url': user.adminUrl,
    'billing-plan': user.plan,
    'user-since': user.userSince,
    'stripe-customer-page': user.stripeUrl
  })

  identifiedEmailAddress = emailAddress
}

/**
 * Drop the identified visitor on sign out. `logout` rather than `reset`: `reset` only empties the
 * contact form, while `logout` clears the identify data, device id and conversation history.
 */
export const resetBeaconUser = () => {
  pendingUser = void 0

  const beacon = getBeacon()
  if (beacon === void 0 || identifiedEmailAddress === void 0) {
    return
  }

  beacon('logout')
  identifiedEmailAddress = void 0
}
